import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useAdminLanguage } from "@/i18n/AdminLanguage";
import { api, fmtErr, fmtDate } from "@/lib/api";

const EMPTY = {
  language: "spanish",
  teacher: "",
  date: "",
  start_time: "",
  end_time: "",
  level: "All levels",
  price: 15,
  currency: "€",
  spots_total: 8,
  active: true,
};

const inputCls =
  "w-full rounded-lg border border-white/15 bg-coal px-3 py-2 text-sm text-white outline-none transition-colors placeholder:text-white/25 focus:border-flame";
const labelCls = "mb-1 block text-[10px] font-bold uppercase tracking-[0.2em] text-white/45";

export default function AdminSlots() {
  const {tr:t}=useAdminLanguage();
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/slots/all")
      .then((r) => setSlots(r.data))
      .catch((e) => toast.error(fmtErr(e)));

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        spots_total: Number(form.spots_total) || 1,
        spots_booked: Number(form.spots_booked) || 0,
      };
      if (form.id) await api.put(`/slots/${form.id}`, payload);
      else await api.post("/slots", payload);
      toast.success(form.id ? t("Slot updated") : t("Slot created"));
      setForm(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("Delete slot?"))) return;
    try {
      await api.delete(`/slots/${id}`);
      toast.success(t("Slot deleted"));
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const toggleActive = async (slot) => {
    try {
      await api.put(`/slots/${slot.id}`, { active: !slot.active });
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div data-testid="admin-slots-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-syne text-xl font-extrabold tracking-tight">{t("Class schedule")}</h2>
          <p className="mt-1 text-xs text-white/40">
            {t("Slots info")}
          </p>
        </div>
        <button
          data-testid="new-slot-btn"
          onClick={() => setForm({ ...EMPTY })}
          className="flex items-center gap-1.5 rounded-full bg-flame px-4 py-2 text-xs font-bold transition-colors hover:bg-ember"
        >
          <Plus className="h-3.5 w-3.5" /> {t("New class slot")}
        </button>
      </div>

      {form && (
        <form data-testid="slot-form" onSubmit={save} className="mt-6 grid gap-4 rounded-xl border border-white/10 bg-ash p-6 md:grid-cols-3">
          <div>
            <label className={labelCls}>{t("Language")}</label>
            <select
              data-testid="slot-language-select"
              value={form.language}
              onChange={(e) => setForm({ ...form, language: e.target.value })}
              className={inputCls}
            >
              <option value="spanish">{t("Spanish")}</option>
              <option value="english">{t("English")}</option>
              <option value="portuguese">{t("Portuguese")}</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>{t("Teacher")}</label>
            <input
              data-testid="slot-teacher-input"
              value={form.teacher}
              onChange={(e) => setForm({ ...form, teacher: e.target.value })}
              placeholder="Lucía"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>{t("Level")}</label>
            <input
              data-testid="slot-level-input"
              value={form.level}
              onChange={(e) => setForm({ ...form, level: e.target.value })}
              placeholder="Beginner / Intermediate / Conversation"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>{t("Date")}</label>
            <input
              data-testid="slot-date-input"
              required
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>{t("Starts")}</label>
            <input
              data-testid="slot-start-input"
              required
              type="time"
              value={form.start_time}
              onChange={(e) => setForm({ ...form, start_time: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>{t("Ends")}</label>
            <input
              data-testid="slot-end-input"
              type="time"
              value={form.end_time}
              onChange={(e) => setForm({ ...form, end_time: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>{t("Price")}</label>
            <input
              data-testid="slot-price-input"
              type="number"
              min="0"
              step="0.5"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>{t("Currency")}</label>
            <input
              data-testid="slot-currency-input"
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>{t("Total spots")}</label>
            <input
              data-testid="slot-spots-input"
              type="number"
              min="1"
              value={form.spots_total}
              onChange={(e) => setForm({ ...form, spots_total: e.target.value })}
              className={inputCls}
            />
          </div>
          <div className="flex gap-3 md:col-span-3">
            <button
              data-testid="slot-save-btn"
              disabled={saving}
              className="rounded-full bg-flame px-6 py-2.5 text-xs font-bold transition-colors hover:bg-ember disabled:opacity-50"
            >
              {saving ? t("Saving…") : form.id ? t("Save changes") : t("New class slot")}
            </button>
            <button
              type="button"
              data-testid="slot-cancel-btn"
              onClick={() => setForm(null)}
              className="flex items-center gap-1 rounded-full border border-white/15 px-5 py-2.5 text-xs font-semibold text-white/60 hover:border-white/40 hover:text-white"
            >
              <X className="h-3.5 w-3.5" /> {t("Cancel")}
            </button>
          </div>
        </form>
      )}

      <div className="mt-6 overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 text-[10px] uppercase tracking-[0.2em] text-white/40">
              <th className="px-4 py-3">{t("Language")}</th>
              <th className="px-4 py-3">{t("Teacher")}</th>
              <th className="px-4 py-3">{t("When")}</th>
              <th className="px-4 py-3">{t("Level")}</th>
              <th className="px-4 py-3">{t("Spots")}</th>
              <th className="px-4 py-3">{t("Status")}</th>
              <th className="px-4 py-3 text-right">{t("Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {slots.map((s) => (
              <tr key={s.id} data-testid={`admin-slot-row-${s.id}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                <td className="px-4 py-3">
                  <span className="rounded-full bg-flame/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-flame">
                    {s.language.slice(0, 2).toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-3 font-semibold">{s.teacher || "—"}</td>
                <td className="px-4 py-3 text-white/55">
                  {fmtDate(s.date)} · {s.start_time}
                  {s.end_time ? `–${s.end_time}` : ""}
                </td>
                <td className="px-4 py-3 text-white/55">{s.level}</td>
                <td className="px-4 py-3 text-white/55">
                  {s.spots_booked}/{s.spots_total}
                </td>
                <td className="px-4 py-3">
                  <button
                    data-testid={`toggle-slot-${s.id}`}
                    onClick={() => toggleActive(s)}
                    className={`rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                      s.active ? "border-flame/50 text-flame hover:bg-flame/10" : "border-white/15 text-white/35 hover:border-white/40"
                    }`}
                  >
                    {s.active ? "Active" : "Hidden"}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button
                      data-testid={`edit-slot-${s.id}`}
                      onClick={() => setForm({ ...s })}
                      className="grid h-8 w-8 place-items-center rounded-full border border-white/15 text-white/60 transition-colors hover:border-flame hover:text-flame"
                      aria-label="Edit slot"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      data-testid={`delete-slot-${s.id}`}
                      onClick={() => remove(s.id)}
                      className="grid h-8 w-8 place-items-center rounded-full border border-white/15 text-white/60 transition-colors hover:border-red-500 hover:text-red-400"
                      aria-label="Delete slot"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {slots.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-white/35">
                  {t("No slots")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
